// Single source of truth for editable storefront content (About, Contact,
// Team, Testimonials, Footer). Stored in `settings/siteContent` — which is
// publicly readable per firestore.rules — and edited from the admin
// "Site Content" page. Components fall back to DEFAULT_CONTENT until/if the
// admin customises a field.

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export const DEFAULT_CONTENT = {
  shipping: {
    // Orders at or above this KSh amount ship free (used by Cart + Checkout,
    // and shown across the storefront). Editable in admin → Site Content.
    freeThreshold: 12000,
    // The scrolling announcement strip at the very top of the storefront.
    announcement: 'Complimentary shipping on orders over KSh 12,000 · Summer sale — up to 50% off',
    // Delivery locations the admin sets — customers pick one at checkout and
    // its fee becomes the shipping cost. Editable in admin → Site Content.
    locations: [
      { name: 'Nairobi CBD', fee: 200 },
      { name: 'Nairobi (outside CBD)', fee: 300 },
      { name: 'Thika', fee: 500 },
      { name: 'Mombasa', fee: 700 },
    ],
  },
  // "More styles on WhatsApp" — a product can carry dozens of variant photos
  // that are impractical to keep uploaded and current. We publish a curated
  // few and invite the shopper to WhatsApp for the rest (and to confirm the
  // style they picked is actually in store). Editable in admin → Site Content.
  variants: {
    enabled: true,
    // How many photos of one product the product page shows before the
    // "more styles" tile takes over.
    galleryLimit: 12,
    title: 'More prints in store',
    message:
      'We carry many more colours and prints than we can show here. Message us on WhatsApp to see the full range — or to confirm this style is in stock in your size before you order.',
    ctaLabel: 'See more on WhatsApp',
    // {product} is replaced with the product name when the shopper taps through.
    whatsappTemplate:
      'Hi! I\'m looking at *{product}* on your website. Could you send me the other prints/colours available, and confirm if this one is in stock? 😊',
  },
  footer: {
    tagline:
      'Your ultimate destination for premium swimwear and beach fashion — crafted for every body, every vibe.',
    email: 'hello@bikiniworldkenya.co.ke',
    phone: '+254 700 000 000',
    location: 'Nairobi, Kenya',
  },
  social: {
    instagram: 'https://instagram.com',
    facebook: 'https://facebook.com',
    twitter: 'https://twitter.com',
    tiktok: 'https://tiktok.com',
    pinterest: 'https://pinterest.com',
  },
  contact: {
    address: 'Kai Plaza, Moi Avenue, Nairobi CBD · Iconic Plaza, Nairobi CBD',
    phone: '+254 726 354 496',
    phoneHours: 'Mon-Sat: 8AM-6PM EAT (closed Sundays)',
    email: 'support@bikiniworld.com',
    email2: 'info@bikiniworld.com',
    hours: 'Mon-Sat: 8AM-6PM EAT (closed Sundays)',
  },
  about: {
    heroTitle: 'Our Story',
    heroSubtitle:
      'From a small passion project to a premier destination for swimwear enthusiasts worldwide',
    stats: [
      { number: '10K+', label: 'Happy Customers' },
      { number: '175+', label: 'Products' },
      { number: '15+', label: 'Brands' },
      { number: '50+', label: 'Cities' },
    ],
    missionTitle: 'Our Mission',
    missionLead:
      'To provide exceptional swimwear that combines style, comfort, and quality at accessible prices.',
    missionBody:
      'At Bikini World, we believe everyone deserves to feel confident at the beach or pool. We carefully curate our collection from the world’s best brands, ensuring every piece meets our high standards for craftsmanship and comfort.',
    storeImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600',
  },
  team: [
    { name: 'Alex Johnson', role: 'Founder & CEO', image: 'https://i.pravatar.cc/150?img=1' },
    { name: 'Sarah Miller', role: 'Head of Operations', image: 'https://i.pravatar.cc/150?img=2' },
    { name: 'Michael Chen', role: 'Marketing Director', image: 'https://i.pravatar.cc/150?img=3' },
    { name: 'Emma Wilson', role: 'Customer Success', image: 'https://i.pravatar.cc/150?img=4' },
  ],
  testimonials: [
    { quote: 'Perfect fit and amazing quality! My absolute go-to beachwear store.', name: 'Emma Wilson', role: 'Verified Buyer', avatar: 'https://i.pravatar.cc/150?img=5', emoji: '🏖️' },
    { quote: 'Fast shipping and the fabric feels incredible. Totally obsessed! ✨', name: 'Isabella Rose', role: 'Verified Buyer', avatar: 'https://i.pravatar.cc/150?img=6', emoji: '🐚' },
    { quote: 'Great variety and the prices are unbeatable. Highly recommended!', name: 'Sophia Lee', role: 'Verified Buyer', avatar: 'https://i.pravatar.cc/150?img=7', emoji: '🌊' },
  ],
  privacy: {
    updated: 'July 2, 2026',
    intro:
      'Bikini World ("we", "us", "our") operates bikiniworldkenya.co.ke (the "Site"). This policy explains what personal information we collect, why we collect it, and how we protect it when you browse, create an account, or place an order with us.',
    sections: [
      {
        heading: '1. Information we collect',
        body:
          "- **Account details:** name, email address and phone number when you register, sign in, or check out — either directly, or via Google Sign-In.\n" +
          "- **Order details:** shipping address, items purchased, order value and order history.\n" +
          "- **Payment information:** when you pay by M-Pesa, we receive your phone number, the M-Pesa transaction/receipt number and the amount paid via Safaricom's Daraja API. We never see or store your M-Pesa PIN, and we do not collect or store card numbers.\n" +
          "- **Communications:** messages you send us via WhatsApp, email or our contact form, so we can respond to enquiries and share order updates.\n" +
          "- **Usage data:** pages viewed, cart contents and general device/browser information, collected automatically to keep the Site working (e.g. so items stay in your cart between visits).",
      },
      {
        heading: '2. How we use your information',
        body:
          "- To create and manage your account, and to process and fulfil your orders.\n" +
          "- To send order confirmations, delivery updates and, where you've opted in, promotional messages.\n" +
          "- To respond to customer support requests, including over WhatsApp.\n" +
          "- To detect and prevent fraud, abuse and unauthorised access to accounts.\n" +
          "- To improve the Site, our product range and the shopping experience.",
      },
      {
        heading: '3. Sign-in with Google',
        body:
          'If you choose "Continue with Google", Google shares your name, email address and profile photo with us to create or sign in to your account. We do not receive your Google password. You can review or revoke Bikini World\'s access at any time from your Google Account permissions page (myaccount.google.com/permissions).',
      },
      {
        heading: '4. Email verification',
        body:
          'When you create an account with an email and password, we send a verification link to confirm the address belongs to you. This helps keep accounts secure and ensures order and delivery updates reach the right inbox.',
      },
      {
        heading: '5. Who we share information with',
        body:
          "- **Safaricom / M-Pesa** — to process mobile money payments.\n" +
          "- **Google Firebase** — our hosting, database, authentication and cloud infrastructure provider, which stores data on our behalf.\n" +
          "- **WhatsApp Business** — to send order updates and respond to support enquiries you initiate.\n" +
          "- **Delivery partners** — your name, phone number and address, solely to deliver your order.\n" +
          "\n" +
          "We do not sell your personal information to third parties.",
      },
      {
        heading: '6. Cookies and local storage',
        body:
          'We use browser storage (cookies/local storage) to keep you signed in, remember your cart, and remember sign-in preferences such as "Remember me". You can clear these at any time through your browser settings, though some features (like a persistent cart) may stop working.',
      },
      {
        heading: '7. Data retention',
        body:
          'We keep account and order records for as long as your account is active, and afterwards for as long as needed to meet our tax, accounting and legal obligations in Kenya.',
      },
      {
        heading: '8. Your rights',
        body:
          'You can access, update or delete most of your account details from your Account page at any time. To request a full copy or deletion of your personal data, or to ask any question about this policy, contact us using the details below.',
      },
      {
        heading: '9. Children',
        body:
          'The Site is intended for users aged 18 and over. We do not knowingly collect information from children.',
      },
      {
        heading: '10. Changes to this policy',
        body:
          'We may update this policy from time to time. Material changes will be reflected by updating the "Last updated" date above.',
      },
    ],
  },
  terms: {
    updated: 'July 2, 2026',
    intro:
      'These Terms of Use ("Terms") govern your access to and use of bikiniworldkenya.co.ke (the "Site") operated by Bikini World. By creating an account, browsing the Site or placing an order, you agree to these Terms. Please also read our Privacy Policy, which explains how we handle your data.',
    sections: [
      {
        heading: '1. Accounts',
        body:
          "- You must provide accurate, current information when creating an account, whether by email/password or via Google Sign-In.\n" +
          "- You are responsible for keeping your password confidential and for all activity under your account.\n" +
          "- We may ask you to verify your email address, and may limit certain features until it is verified.\n" +
          "- We may suspend or terminate accounts used for fraud, abuse or violation of these Terms.",
      },
      {
        heading: '2. Products, pricing & availability',
        body:
          'Prices are shown in Kenyan Shillings (KSh) and may change without notice. We make reasonable efforts to display accurate product information and photos, but colours, fit and fabric may vary slightly from images shown, including AI-generated styling images used for marketing purposes. Availability is not guaranteed until an order is confirmed.',
      },
      {
        heading: '3. Orders & payment',
        body:
          "- Placing an order is an offer to purchase, which we may accept or decline (e.g. due to stock or pricing errors).\n" +
          "- We accept M-Pesa and, where offered, cash on delivery. M-Pesa payments are processed via Safaricom's Daraja platform — we never see or store your M-Pesa PIN.\n" +
          "- Orders are only confirmed once payment is received or, for cash on delivery, once the order is accepted.",
      },
      {
        heading: '4. Shipping & delivery',
        body:
          'We deliver within Kenya. Delivery timeframes shown at checkout are estimates, not guarantees. Risk of loss passes to you once an order is delivered to the address you provided.',
      },
      {
        heading: '5. Returns & refunds',
        body:
          'Due to hygiene reasons, swimwear can only be returned if unworn, unwashed, with all original tags and hygiene liners attached, within 7 days of delivery. Contact us via WhatsApp or email to start a return. Approved refunds are issued to the original payment method or as store credit, at our discretion.',
      },
      {
        heading: '6. Acceptable use',
        body:
          "You agree not to:\n" +
          "- Use the Site for any unlawful purpose or to defraud us or other customers;\n" +
          "- Attempt to gain unauthorised access to accounts, systems or data on the Site;\n" +
          "- Interfere with the normal operation of the Site (e.g. scraping, overloading, or bypassing security controls);\n" +
          "- Post or submit false, misleading or infringing content (including product reviews).",
      },
      {
        heading: '7. Intellectual property',
        body:
          'All content on the Site — including text, graphics, logos and product photography (including AI-generated styling images) — is owned by or licensed to Bikini World and may not be copied or reused without our permission.',
      },
      {
        heading: '8. Third-party services',
        body:
          'The Site uses Google Firebase for hosting and infrastructure, Google Sign-In for authentication, Safaricom M-Pesa for payments, and WhatsApp for order updates and support. Your use of these features is also subject to those providers\' own terms.',
      },
      {
        heading: '9. Limitation of liability',
        body:
          'To the fullest extent permitted by law, Bikini World is not liable for indirect, incidental or consequential damages arising from your use of the Site. Our total liability for any claim is limited to the amount you paid for the order giving rise to the claim.',
      },
      {
        heading: '10. Governing law',
        body:
          'These Terms are governed by the laws of Kenya. Any disputes will be subject to the exclusive jurisdiction of the courts of Kenya.',
      },
      {
        heading: '11. Changes to these Terms',
        body:
          'We may update these Terms from time to time. Continued use of the Site after changes are posted means you accept the updated Terms.',
      },
    ],
  },
};

// Shallow-per-section merge: a stored section replaces only the keys it defines;
// arrays (team, testimonials, stats) are replaced wholesale when present.
export function mergeContent(stored) {
  if (!stored) return DEFAULT_CONTENT;
  const out = { ...DEFAULT_CONTENT };
  for (const key of Object.keys(DEFAULT_CONTENT)) {
    const def = DEFAULT_CONTENT[key];
    const val = stored[key];
    if (val == null) continue;
    out[key] = Array.isArray(def) ? val : { ...def, ...val };
  }
  return out;
}

export function useSiteContent() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'siteContent'));
        if (alive && snap.exists()) setContent(mergeContent(snap.data()));
      } catch (e) {
        console.error('Site content load failed:', e);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  return { content, loaded };
}

// ── Shipping helpers ─────────────────────────────────────────────────────────
// Shared so the free-shipping threshold reads consistently everywhere (and
// updates the moment an admin changes it in Site Content).
export const kshAmount = (n) => `KSh ${Number(n || 0).toLocaleString()}`;

export const freeShippingText = (threshold) =>
  `Free shipping on orders over ${kshAmount(threshold)}`;
